/**
 * Entity mutation hooks using generic factory
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { entityApi } from '@/lib/api/entities.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { entityModuleCountsKey } from '../useEntityModuleCounts';
import { getEntityTerminologyStatic } from '@/lib/terminology/static-terminology';
import { toast } from 'sonner';
import type { Entity, CreateEntity, UpdateEntity } from '@scrambl/shared';

const mutations = createResourceMutations<Entity, CreateEntity, UpdateEntity>({
  resourceName: 'Entity',
  resourceNameSingular: 'entity',
  getLabels: () => getEntityTerminologyStatic(),
  queryKeyFactory: queryKeys.entities,
  api: {
    create: (data: CreateEntity) => entityApi.create(data),
    update: (id: string, data: UpdateEntity) => entityApi.update(id, data),
    updateField: (id: string, field: string, value: unknown) =>
      entityApi.updateEntityField({ entityId: id, field, value }),
    delete: (id: string) => entityApi.delete(id),
  },
  enableRealtime: false,
  invalidation: {
    onCreate: { invalidateRelated: [[...entityModuleCountsKey]] },
    onDelete: { invalidateRelated: [[...entityModuleCountsKey]] },
  },
});

export const useCreateEntityMutation = mutations.useCreateMutation;
export const useUpdateEntityMutation = mutations.useUpdateMutation;
export const useUpdateEntityFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteEntityMutation = mutations.useDeleteMutation;

/**
 * Bulk delete entities mutation hook
 */
export function useBulkDeleteEntitiesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => entityApi.bulkDeleteEntities(ids),
    onSuccess: (result) => {
      const { singular, plural } = getEntityTerminologyStatic();
      toast.success(`${result.deletedCount} ${result.deletedCount === 1 ? singular : plural} deleted`);
      // Invalidate entities list and module counts
      queryClient.invalidateQueries({ queryKey: queryKeys.entities.lists() });
      queryClient.invalidateQueries({ queryKey: entityModuleCountsKey });
    },
    onError: (error) => {
      toast.error(`Failed to delete ${getEntityTerminologyStatic().plural}`);
      console.error('Bulk delete entities error:', error);
    },
  });
}
